import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HeaderComponent } from '../../../shared/header/header.component';
import { CashRegisterService } from '../../../../services/cash-register/cash-register.service';
import { PaymentService } from '../../../../services/payment/payment.service';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import {
  CancelledReceipt,
  CashRegister,
  CashRegisterBalance,
  CashRegisterBalanceTransaction,
} from '../../../../interfaces/cash-register';
import { brandNameOrDefault, formatBRL, formatDateTimeBR, openReportPrintWindow, receiptLogoHtml } from '../../../../utils/print-receipt.util';
import { BrandService } from '../../../../services/brand/brand.service';

@Component({
  selector: 'app-historico',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, RouterLink],
  templateUrl: './historico.component.html',
})
export class HistoricoComponent implements OnInit {
  caixas: CashRegister[] = [];
  caixaSelecionada: CashRegister | null = null;

  dataInicio = '';
  dataFim = '';

  saldos: CashRegisterBalance[] = [];
  isLoading = false;
  erro = '';

  saldoSelecionado: CashRegisterBalance | null = null;
  transacoes: CashRegisterBalanceTransaction[] = [];
  cancelamentos: CancelledReceipt[] = [];
  isLoadingTransacoes = false;
  isFechandoSaldo = false;
  showConfirmFechamento = false;
  feedbackFechamento = '';
  private username = '';

  constructor(
    private router: Router,
    private cashRegisterService: CashRegisterService,
    private paymentService: PaymentService,
    private localStorage: LocalStorageService,
    private brandService: BrandService,
  ) {}

  async ngOnInit() {
    const username = this.localStorage.get('login') as string;
    const unit = Number(this.localStorage.get('currentUnit'));

    if (!username || !unit) {
      this.router.navigate(['/home']);
      return;
    }

    this.username = username;
    this.caixas = await this.cashRegisterService.getByUnit(unit, username);
    this.caixaSelecionada = this.caixas[0] ?? null;

    const hoje = new Date();
    const seteDiasAtras = new Date(hoje);
    seteDiasAtras.setDate(hoje.getDate() - 7);
    this.dataInicio = this.toDateInput(seteDiasAtras);
    this.dataFim = this.toDateInput(hoje);

    if (this.caixaSelecionada) await this.buscar();
  }

  async buscar() {
    if (!this.caixaSelecionada || this.isLoading) return;
    if (!this.dataInicio || !this.dataFim) {
      this.erro = 'Informe data inicial e final.';
      return;
    }

    this.isLoading = true;
    this.erro = '';
    this.saldoSelecionado = null;
    this.transacoes = [];
    try {
      this.saldos = await this.paymentService.getBalanceHistory(
        this.caixaSelecionada.sequence,
        this.dataInicio,
        this.dataFim,
      );
      if (this.saldos.length === 0) {
        this.erro = 'Nenhum saldo encontrado no período.';
      }
    } catch (e: any) {
      this.erro = e?.message || 'Erro ao buscar histórico.';
    } finally {
      this.isLoading = false;
    }
  }

  async verTransacoes(saldo: CashRegisterBalance) {
    this.saldoSelecionado = saldo;
    this.transacoes = [];
    this.cancelamentos = [];
    this.isLoadingTransacoes = true;
    try {
      const [tx, canc] = await Promise.all([
        this.paymentService.getBalanceTransactions(saldo.id),
        this.paymentService.getCancelledReceipts(saldo.id),
      ]);
      this.transacoes = tx;
      this.cancelamentos = canc;
    } finally {
      this.isLoadingTransacoes = false;
    }
  }

  voltarListaSaldos() {
    this.saldoSelecionado = null;
    this.transacoes = [];
    this.cancelamentos = [];
  }

  voltar() {
    this.router.navigate(['/lancamentos-financeiros']);
  }

  abrirConfirmFechamento() {
    if (!this.saldoSelecionado || this.saldoSelecionado.closureDate) return;
    this.feedbackFechamento = '';
    this.showConfirmFechamento = true;
  }

  fecharConfirmFechamento() {
    if (this.isFechandoSaldo) return;
    this.showConfirmFechamento = false;
  }

  async confirmarFecharSaldo() {
    if (!this.saldoSelecionado || this.isFechandoSaldo) return;
    this.isFechandoSaldo = true;
    this.feedbackFechamento = '';
    try {
      await this.cashRegisterService.closeBalance(this.saldoSelecionado.id, this.username);
      this.feedbackFechamento = 'Saldo fechado com sucesso.';
      this.showConfirmFechamento = false;
      // Recarrega a lista e o drill-down do mesmo saldo
      const balanceId = this.saldoSelecionado.id;
      await this.buscar();
      const atualizado = this.saldos.find(s => s.id === balanceId);
      if (atualizado) await this.verTransacoes(atualizado);
    } catch (e: any) {
      this.feedbackFechamento = e?.error?.feedback || e?.message || 'Erro ao fechar saldo.';
    } finally {
      this.isFechandoSaldo = false;
    }
  }

  baixarPdf() {
    if (!this.saldoSelecionado || !this.caixaSelecionada) return;
    const s = this.saldoSelecionado;
    const caixa = this.caixaSelecionada;
    const dataSaldo = new Date(s.balanceDate).toLocaleDateString('pt-BR');

    // Totais de entradas e saídas (sangrias contam como saída)
    let totalEntradas = 0;
    let totalSaidas = 0;
    for (const t of this.transacoes) {
      const val = t.vlTransacao || 0;
      if (this.isSangria(t)) totalSaidas += val; else totalEntradas += val;
    }

    const rows = this.transacoes.map(t => {
      const sangria = this.isSangria(t);
      const prefix = sangria ? '- ' : '';
      const color = sangria ? 'color:#dc2626' : 'color:#7b35cb';
      return `
      <tr>
        <td>${t.dtAtualizacao ? formatDateTimeBR(t.dtAtualizacao) : '—'}</td>
        <td>${t.dsCaixa || ''}</td>
        <td>${t.dsTransacao || ''}</td>
        <td class="right" style="${color}; font-weight: 700;">${prefix}${formatBRL(t.vlTransacao || 0)}</td>
        <td>${t.nome || (t.dsObservacao ? `<span class="muted">${t.dsObservacao}</span>` : '')}${t.dsCaixaDestino ? `<div class="muted">→ ${t.dsCaixaDestino}</div>` : ''}</td>
        <td class="right">${t.nrRecibo ?? '—'}</td>
      </tr>`;
    }).join('');

    const totalCancelados = this.cancelamentos.reduce((acc, c) => acc + (c.vlEspecie || 0), 0);
    const cancelRows = this.cancelamentos.map(c => `
      <tr>
        <td>${c.dtCancelamento ? formatDateTimeBR(c.dtCancelamento) : '—'}</td>
        <td>${c.dsTransacao || ''}</td>
        <td class="right">${formatBRL(c.vlEspecie || 0)}</td>
        <td>${c.nmPaciente || '—'}</td>
        <td>${c.nmUsuario || '—'}</td>
        <td class="right">${c.nrRecibo ?? '—'}</td>
      </tr>
    `).join('');

    const cancelSection = this.cancelamentos.length > 0 ? `
      <h3 style="margin: 18px 0 6px 0; font-size: 12px; color: #991b1b;">Cancelamentos</h3>
      <table class="rtable">
        <thead>
          <tr>
            <th>Data Cancelamento</th>
            <th>Transação</th>
            <th class="right">Valor</th>
            <th>Paciente</th>
            <th>Usuário</th>
            <th class="right">Recibo</th>
          </tr>
        </thead>
        <tbody>
          ${cancelRows}
          <tr class="total">
            <td colspan="2" class="right">Total cancelado (${this.cancelamentos.length}):</td>
            <td class="right">${formatBRL(totalCancelados)}</td>
            <td colspan="3"></td>
          </tr>
        </tbody>
      </table>
    ` : '';

    const html = `
      <div class="report-header">
        <div class="logo">${receiptLogoHtml(this.brandService.logo, this.brandService.cdEmpresa)}</div>
        <div class="meta">
          <div class="title">Histórico de Recebimentos em Espécie</div>
          <div>Caixa: <strong>${caixa.name}</strong> (${caixa.establishmentName})</div>
          <div>Saldo do dia: <strong>${dataSaldo}</strong> — seq ${s.id}</div>
          <div class="muted">Emitido em ${formatDateTimeBR(new Date())}</div>
        </div>
      </div>
      <div class="report-subtitle">
        Saldo inicial: <strong>${formatBRL(s.initialBalance)}</strong> &nbsp;·&nbsp;
        Entradas: <strong style="color:#00AC70">${formatBRL(s.ingressBalance)}</strong> &nbsp;·&nbsp;
        Saídas: <strong style="color:#991b1b">${formatBRL(s.egressBalance)}</strong> &nbsp;·&nbsp;
        Saldo atual: <strong style="color:#7b35cb">${formatBRL(s.currentBalance)}</strong>
      </div>
      <table class="rtable">
        <thead>
          <tr>
            <th>Data</th>
            <th>Tipo</th>
            <th>Transação</th>
            <th class="right">Valor</th>
            <th>Pessoa / Observação</th>
            <th class="right">Recibo</th>
          </tr>
        </thead>
        <tbody>
          ${rows || '<tr><td colspan="6" class="center muted">Nenhuma transação registrada.</td></tr>'}
          ${this.transacoes.length > 0 ? `
          <tr class="total">
            <td colspan="3" class="right" style="font-weight: 700;">Entradas:</td>
            <td class="right" style="color:#00AC70; font-weight: 700;">${formatBRL(totalEntradas)}</td>
            <td colspan="2"></td>
          </tr>
          <tr class="total">
            <td colspan="3" class="right" style="font-weight: 700;">Saídas:</td>
            <td class="right" style="color:#dc2626; font-weight: 700;">- ${formatBRL(totalSaidas)}</td>
            <td colspan="2"></td>
          </tr>` : ''}
        </tbody>
      </table>
      ${cancelSection}
      <div class="footer">${brandNameOrDefault(this.brandService.brandName)} — relatório gerado pelo Flua Pré-Atendimento</div>
    `;
    openReportPrintWindow(`Historico_${caixa.name}_${dataSaldo}`, html);
  }

  formatCurrency(value: number): string {
    return value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) ?? 'R$ 0,00';
  }

  isSangria(t: CashRegisterBalanceTransaction): boolean {
    return t?.dsCaixa === 'Trans. Caixa';
  }

  private toDateInput(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
}
